import { useState, useRef, useEffect } from "react";
import styles from "./MultiSelectDropdown.module.css";

const MultiSelectDropdown = ({
  options = [],
  selectedValues = [],
  onChange,
  placeholder = "Select options...",
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleToggleOption = (option) => {
    const isSelected = selectedValues.includes(option);
    const newSelectedValues = isSelected
      ? selectedValues.filter((value) => value !== option)
      : [...selectedValues, option];

    onChange(newSelectedValues);
  };

  const handleRemoveTag = (optionToRemove, event) => {
    event.stopPropagation();
    onChange(selectedValues.filter((value) => value !== optionToRemove));
  };

  const toggleDropdown = () => {
    setIsOpen((open) => !open);
  };

  const rootClass = [styles.multiSelectDropdown, className]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={rootClass} ref={dropdownRef}>
      <div
        className={`${styles.dropdownTrigger} ${isOpen ? styles.open : ""}`}
        onClick={toggleDropdown}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleDropdown();
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <div className={styles.selectedValues}>
          {selectedValues.length === 0 ? (
            <span className={styles.placeholder}>{placeholder}</span>
          ) : (
            <div className={styles.tags}>
              {selectedValues.map((value) => (
                <span key={value} className={styles.tag}>
                  {value}
                  <button
                    type="button"
                    className={styles.removeTag}
                    onClick={(e) => handleRemoveTag(value, e)}
                    aria-label={`Remove ${value}`}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
        <div
          className={`${styles.arrow} ${
            isOpen ? styles.arrowUp : styles.arrowDown
          }`}
          aria-hidden
        >
          {"\u25BC"}
        </div>
      </div>

      {isOpen && (
        <div className={styles.dropdownMenu} role="listbox" aria-multiselectable>
          <div className={styles.optionsList}>
            {options.map((option, index) => (
              <label
                key={option}
                className={`${styles.option} ${
                  selectedValues.includes(option) ? styles.selected : ""
                }`}
                htmlFor={`msd-opt-${index}`}
              >
                <input
                  id={`msd-opt-${index}`}
                  type="checkbox"
                  checked={selectedValues.includes(option)}
                  onChange={() => handleToggleOption(option)}
                  className={styles.checkbox}
                />
                <span className={styles.optionText}>{option}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
